/**
 * Server Broadcast contract (Phase 1) — channel names + event types + channel fan-out.
 * Payloads are trigger-only (IDs / branch / timestamp). No PII or row bodies.
 *
 * Audit action_type mapping: see docs/BROADCAST_AUDIT_ALIGNMENT.md
 * (e.g. broadcast `clawback_collected` ↔ audit `COMMISSION_CLAWBACK_COLLECTED`).
 */

/** @typedef {typeof BROADCAST_EVENTS[keyof typeof BROADCAST_EVENTS]} BroadcastEventType */

export const BROADCAST_EVENTS = Object.freeze({
  REFUND_COMPLETED: "refund_completed",
  CHECKOUT_COMPLETED: "checkout_completed",
  BILL_EDITED: "bill_edited",
  BILL_VOIDED: "bill_voided",
  COMMISSION_SETTLED: "commission_settled",
  CLAWBACK_CREATED: "clawback_created",
  CLAWBACK_COLLECTED: "clawback_collected",
  APPOINTMENT_CREATED: "appointment_created",
  APPOINTMENT_STATUS_CHANGED: "appointment_status_changed",
  APPOINTMENT_RESCHEDULED: "appointment_rescheduled",
  WALLET_TOPPED_UP: "wallet_topped_up",
  BILLING_SESSION_CHANGED: "billing_session_changed",
  MANUAL_DISCOUNT_APPLIED: "manual_discount_applied",
  OFFER_APPLIED: "offer_applied",
  NO_COMMISSION_APPLIED: "no_commission_applied",
  MEMBERSHIP_ASSIGNED: "membership_assigned",
  ATTENDANCE_MARKED: "attendance_marked",
  WAITLIST_CHANGED: "waitlist_changed",
  ADVANCE_ISSUED: "advance_issued",
  PAYROLL_DEDUCTION: "payroll_deduction",
  SALARY_PAID: "salary_paid",
  PAYROLL_PERIOD_CLOSED: "payroll_period_closed",
  INVENTORY_CHANGED: "inventory_changed",
  /** Coarse settings invalidate — payload.domain selects SWR/cache slice. */
  SETTINGS_CHANGED: "settings_changed",
  /** Campaign status / recipient counters changed (CRM campaigns). */
  CAMPAIGN_CHANGED: "campaign_changed",
  /** Plan, suite, or subscription status changed — invalidate entitlement caches. */
  SUBSCRIPTION_CHANGED: "subscription_changed",
  /** WhatsApp monthly usage counters changed. */
  WHATSAPP_USAGE_CHANGED: "whatsapp_usage_changed",
  /** Founding 100 public slot counter changed. */
  FOUNDING_SLOT_CLAIMED: "founding_slot_claimed",
});

/** Allowed `domain` values for SETTINGS_CHANGED (client-side cache map). */
export const SETTINGS_CHANGED_DOMAINS = Object.freeze([
  "tax",
  "wallet",
  "commission",
  "catalog",
  "branch_hours",
  "upi",
  "staff_policy",
  "loyalty",
  "offer",
  "offer_disabled",
  "subscription",
]);

/** Whitelist of allowed payload keys (zero-PII / trigger-only). */
export const ALLOWED_BROADCAST_PAYLOAD_KEYS = Object.freeze([
  "invoice_id",
  "invoice_number",
  "credit_note_id",
  "credit_note_number",
  "branch_id",
  "salon_id",
  "timestamp",
  "employee_id",
  "amount",
  "settlement_id",
  "clawback_id",
  "appointment_id",
  "new_status",
  "client_id",
  "session_id",
  "membership_id",
  "action",
  "amount_paise",
  "advance_id",
  "deduction_id",
  "reason",
  "period_start",
  "period_end",
  "product_id",
  "batch_size",
  "mode",
  "domain",
  "campaign_id",
  "change_type",
  "new_plan",
  "suite_id",
  "new_suites",
  "staff_count",
  "branch_count",
  "billing_cycle",
  "whatsapp_quotas",
  "utility_used",
  "marketing_used",
  "overage_paise",
  "slots_remaining",
  "slots_claimed",
  "slots_total",
  "founding_active",
  "offer_id",
  "crm_offer_id",
]);

export function dashboardChannel(branchId) {
  return `dashboard:branch:${Number(branchId)}`;
}

export function crmChannel(branchId) {
  return `crm:branch:${Number(branchId)}`;
}

export function receptionChannel(branchId) {
  return `reception:branch:${Number(branchId)}`;
}

export function staffChannel(employeeId) {
  return `staff:employee:${Number(employeeId)}`;
}

export function settingsChannel(salonId) {
  return `settings:salon:${Number(salonId)}`;
}

/** Public (unauthenticated) founding counter channel for landing + pricing pages. */
export function foundingStatusPublicChannel() {
  return "founding-status:public";
}

/**
 * Normalize positive int ids from a single value or array.
 * @param {unknown} raw
 * @returns {number[]}
 */
function normalizePositiveIds(raw) {
  const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    const n = Number(item);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Build channel list for an event + RBAC context.
 *
 * @param {string} eventType
 * @param {{
 *   branchId?: number|string|null,
 *   employeeId?: number|string|null,
 *   employeeIds?: Array<number|string>|null,
 * }} [context]
 * @returns {string[]}
 */
export function buildBroadcastChannelsForEvent(eventType, context = {}) {
  const branchId = Number(context?.branchId);
  const hasBranch = Number.isFinite(branchId) && branchId > 0;
  const employeeIds = normalizePositiveIds([
    ...(Array.isArray(context?.employeeIds) ? context.employeeIds : []),
    context?.employeeId,
  ]);

  const channels = [];
  const pushUnique = (name) => {
    if (name && !channels.includes(name)) channels.push(name);
  };

  const branchSurfaces = () => {
    if (!hasBranch) return;
    pushUnique(dashboardChannel(branchId));
    pushUnique(crmChannel(branchId));
    pushUnique(receptionChannel(branchId));
  };

  const staffSurfaces = () => {
    for (const eid of employeeIds) pushUnique(staffChannel(eid));
  };

  switch (eventType) {
    case BROADCAST_EVENTS.REFUND_COMPLETED:
    case BROADCAST_EVENTS.CHECKOUT_COMPLETED:
    case BROADCAST_EVENTS.BILL_EDITED:
    case BROADCAST_EVENTS.BILL_VOIDED:
    case BROADCAST_EVENTS.MANUAL_DISCOUNT_APPLIED:
    case BROADCAST_EVENTS.OFFER_APPLIED:
      branchSurfaces();
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.NO_COMMISSION_APPLIED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(crmChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.COMMISSION_SETTLED:
    case BROADCAST_EVENTS.CLAWBACK_CREATED:
    case BROADCAST_EVENTS.CLAWBACK_COLLECTED:
      if (hasBranch) pushUnique(dashboardChannel(branchId));
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.APPOINTMENT_CREATED:
    case BROADCAST_EVENTS.APPOINTMENT_STATUS_CHANGED:
    case BROADCAST_EVENTS.APPOINTMENT_RESCHEDULED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
      }
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.BILLING_SESSION_CHANGED:
      if (hasBranch) pushUnique(receptionChannel(branchId));
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.WALLET_TOPPED_UP:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
        pushUnique(crmChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.MEMBERSHIP_ASSIGNED:
      if (hasBranch) {
        pushUnique(crmChannel(branchId));
        pushUnique(receptionChannel(branchId));
        pushUnique(dashboardChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.ATTENDANCE_MARKED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
      }
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.WAITLIST_CHANGED:
      if (hasBranch) pushUnique(receptionChannel(branchId));
      break;
    case BROADCAST_EVENTS.ADVANCE_ISSUED:
    case BROADCAST_EVENTS.PAYROLL_DEDUCTION:
    case BROADCAST_EVENTS.SALARY_PAID:
      if (hasBranch) pushUnique(dashboardChannel(branchId));
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.PAYROLL_PERIOD_CLOSED:
      if (hasBranch) pushUnique(dashboardChannel(branchId));
      break;
    case BROADCAST_EVENTS.INVENTORY_CHANGED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.SETTINGS_CHANGED:
      // Owner dashboards + reception floor (tax/wallet/catalog/hours).
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.CAMPAIGN_CHANGED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(crmChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.SUBSCRIPTION_CHANGED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(receptionChannel(branchId));
        pushUnique(crmChannel(branchId));
      }
      // Staff Pay gates on Workforce — fan out when employeeIds provided.
      staffSurfaces();
      break;
    case BROADCAST_EVENTS.WHATSAPP_USAGE_CHANGED:
      if (hasBranch) {
        pushUnique(dashboardChannel(branchId));
        pushUnique(crmChannel(branchId));
        pushUnique(receptionChannel(branchId));
      }
      break;
    case BROADCAST_EVENTS.FOUNDING_SLOT_CLAIMED:
      pushUnique(foundingStatusPublicChannel());
      break;
    default:
      break;
  }

  return channels;
}
